import React, { useEffect, useState } from 'react';
import {
  siigoTest, siigoPreviewClientes, siigoImportClientes,
  siigoDocsResumen, siigoImportDocs, siigoListDocs, siigoGetDocPdfBlob,
  siigoGenerarPdfsCotiz, siigoPreviewProductos, siigoImportProductos,
} from '../services/api';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(n || 0);
const RES_KEY = { factura:'facturas', cotizacion:'cotizaciones', nota_credito:'notas_credito' };
const TIPO_LABEL = { factura:'facturas', cotizacion:'cotizaciones', nota_credito:'notas crédito' };

export default function ImportarSiigo() {
  const [estado, setEstado] = useState('');
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingCli, setLoadingCli] = useState(false);
  const [resCli, setResCli] = useState(null);

  const [resumen, setResumen] = useState(null);       // { facturas:{total,importadas}, cotizaciones:{...} }
  const [imp, setImp] = useState({});                 // progreso por tipo
  const [histTipo, setHistTipo] = useState('factura');
  const [hist, setHist] = useState(null);             // { total, page, documentos }
  const [pdfGen, setPdfGen] = useState(null);         // progreso generación PDFs cotizaciones
  const [previewProd, setPreviewProd] = useState(null);
  const [loadingProd, setLoadingProd] = useState(false);
  const [resProd, setResProd] = useState(null);

  useEffect(() => { cargarResumen(); }, []);
  useEffect(() => { cargarHistorial(1); /* eslint-disable-next-line */ }, [histTipo]);

  const cargarResumen = async () => {
    try { const { data } = await siigoDocsResumen(); setResumen(data); } catch { /* silencio */ }
  };
  const cargarHistorial = async (page = 1) => {
    try { const { data } = await siigoListDocs(histTipo, page); setHist(data); } catch { setHist(null); }
  };

  const probar = async () => {
    setEstado('probando'); setMsg('');
    try { const { data } = await siigoTest(); setEstado('ok'); setMsg(data.mensaje || 'Conexión exitosa'); }
    catch (err) { setEstado('error'); setMsg(err.response?.data?.error || 'No se pudo conectar'); }
  };

  const previsualizar = async () => {
    setLoadingCli(true); setResCli(null); setPreview(null);
    try { const { data } = await siigoPreviewClientes(); setPreview(data); }
    catch (err) { setEstado('error'); setMsg(err.response?.data?.error || 'Error al previsualizar'); }
    finally { setLoadingCli(false); }
  };
  const importarClientes = async () => {
    if (!window.confirm(`Se importarán ${preview.nuevos} clientes nuevos. ¿Continuar?`)) return;
    setLoadingCli(true);
    try {
      const { data } = await siigoImportClientes(); setResCli(data);
      const { data: p } = await siigoPreviewClientes(); setPreview(p);
    } catch (err) { setEstado('error'); setMsg(err.response?.data?.error || 'Error al importar'); }
    finally { setLoadingCli(false); }
  };

  const previsualizarProd = async () => {
    setLoadingProd(true); setResProd(null); setPreviewProd(null);
    try { const { data } = await siigoPreviewProductos(); setPreviewProd(data); }
    catch (err) { setEstado('error'); setMsg(err.response?.data?.error || 'Error al previsualizar productos'); }
    finally { setLoadingProd(false); }
  };
  const importarProd = async () => {
    if (!window.confirm(`Se importarán ${previewProd.nuevos} productos nuevos. ¿Continuar?`)) return;
    setLoadingProd(true);
    try {
      const { data } = await siigoImportProductos(); setResProd(data);
      const { data: p } = await siigoPreviewProductos(); setPreviewProd(p);
    } catch (err) { setEstado('error'); setMsg(err.response?.data?.error || 'Error al importar productos'); }
    finally { setLoadingProd(false); }
  };

  const importarDocs = async (tipo) => {
    const totalSiigo = resumen?.[RES_KEY[tipo]]?.total || 0;
    if (!window.confirm(`Se traerá el historial de ${TIPO_LABEL[tipo]} desde Siigo (${totalSiigo} documentos). Puede tardar unos minutos. ¿Continuar?`)) return;
    let page = 1, importados = 0, omitidos = 0, errores = 0, total = totalSiigo, traidosAcum = 0;
    setImp(prev => ({ ...prev, [tipo]: { corriendo: true, importados, omitidos, total } }));
    try {
      while (true) {
        const { data } = await siigoImportDocs(tipo, page);
        importados += data.importados; omitidos += data.omitidos; errores += data.errores; total = data.total;
        traidosAcum += (data.traidos || 0);
        setImp(prev => ({ ...prev, [tipo]: { corriendo: true, importados, omitidos, errores, total, hechos: traidosAcum } }));
        if (!data.traidos || traidosAcum >= total) break;
        page += 1;
      }
      setImp(prev => ({ ...prev, [tipo]: { corriendo: false, importados, omitidos, errores, total, done: true } }));
      await cargarResumen();
      if (histTipo === tipo) await cargarHistorial(1);
    } catch (err) {
      setImp(prev => ({ ...prev, [tipo]: { corriendo: false, error: err.response?.data?.error || 'Error al importar' } }));
    }
  };

  const generarPdfsCotiz = async () => {
    const totalPend = resumen?.cotizaciones?.sin_pdf || 0;
    if (!totalPend) return;
    if (!window.confirm(`Se generarán PDF autocontenidos de ${totalPend} cotizaciones (renderizando desde Siigo). Tarda ~6s cada una. ¿Continuar?`)) return;
    let generados = 0, errores = 0, restantes = totalPend;
    setPdfGen({ corriendo: true, generados, total: totalPend });
    try {
      while (restantes > 0) {
        const { data } = await siigoGenerarPdfsCotiz();
        generados += data.generados; errores += (data.errores || 0); restantes = data.restantes;
        setPdfGen({ corriendo: true, generados, errores, total: totalPend, restantes });
        if (data.generados === 0 && (data.errores || 0) === 0) break; // nada avanzó: evitar bucle
      }
      setPdfGen({ corriendo: false, generados, errores, total: totalPend, done: true });
      await cargarResumen();
    } catch (err) {
      setPdfGen({ corriendo: false, error: err.response?.data?.error || 'Error generando PDFs' });
    }
  };

  const verDoc = async (doc) => {
    if (!doc.pdf_path && doc.public_url) { window.open(doc.public_url, '_blank'); return; }
    try {
      const r = await siigoGetDocPdfBlob(doc.id);
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { alert('No se pudo abrir el documento'); }
  };

  const DocSection = ({ tipo, titulo, nota }) => {
    const r = resumen?.[RES_KEY[tipo]];
    const p = imp[tipo];
    const pct = p && p.total ? Math.min(100, Math.round((p.hechos || 0) / p.total * 100)) : 0;
    return (
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.h3}>{titulo}</h3>
          <button style={st.btnSec} onClick={() => importarDocs(tipo)} disabled={p?.corriendo || !r}>
            {p?.corriendo ? 'Importando…' : 'Importar historial'}
          </button>
        </div>
        {nota && <p style={st.nota}>{nota}</p>}
        {r && (
          <div style={st.stats}>
            <Stat label="En Siigo" value={r.total} color="#94a3b8" />
            <Stat label="Ya importadas" value={r.importadas} color="#10b981" />
          </div>
        )}
        {p?.corriendo && (
          <div style={{ marginTop:14 }}>
            <div style={st.barBg}><div style={{ ...st.barFill, width:`${pct}%` }} /></div>
            <p style={st.hint}>{p.hechos || 0} de {p.total} · {p.importados} nuevas · {p.omitidos} ya existían · {pct}%</p>
          </div>
        )}
        {p?.done && <div style={st.ok}>✅ Listo · {p.importados} importadas · {p.omitidos} ya existían{p.errores ? ` · ${p.errores} errores` : ''}</div>}
        {p?.error && <div style={st.err}>❌ {p.error}</div>}

        {tipo === 'cotizacion' && r && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #334155' }}>
            <div style={st.cardHead}>
              <div>
                <div style={{ color:'#f1f5f9', fontSize:13, fontWeight:600 }}>PDF autocontenido</div>
                <p style={st.nota}>Guarda cada cotización como PDF propio (independiente de Siigo). {r.sin_pdf > 0 ? `${r.sin_pdf} sin PDF.` : 'Todas tienen PDF ✅'}</p>
              </div>
              {r.sin_pdf > 0 && (
                <button style={st.btnSec} onClick={generarPdfsCotiz} disabled={pdfGen?.corriendo}>
                  {pdfGen?.corriendo ? 'Generando…' : `Generar ${r.sin_pdf} PDFs`}
                </button>
              )}
            </div>
            {pdfGen?.corriendo && (
              <div style={{ marginTop:12 }}>
                <div style={st.barBg}><div style={{ ...st.barFill, width:`${pdfGen.total ? Math.round(pdfGen.generados / pdfGen.total * 100) : 0}%` }} /></div>
                <p style={st.hint}>{pdfGen.generados} de {pdfGen.total} PDFs generados</p>
              </div>
            )}
            {pdfGen?.done && <div style={st.ok}>✅ {pdfGen.generados} PDFs generados{pdfGen.errores ? ` · ${pdfGen.errores} con error` : ''}</div>}
            {pdfGen?.error && <div style={st.err}>❌ {pdfGen.error}</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1050 }}>
      <h1 style={st.h1}>🔄 Importar desde Siigo</h1>
      <p style={st.sub}>Trae tus clientes y el historial de facturas y cotizaciones desde Siigo Nube. No se duplica nada.</p>

      {/* 1. Conexión */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.h3}>1. Conexión con Siigo</h3>
          <button style={st.btnSec} onClick={probar} disabled={estado === 'probando'}>
            {estado === 'probando' ? 'Probando…' : 'Probar conexión'}
          </button>
        </div>
        {estado === 'ok'    && <div style={st.ok}>✅ {msg}</div>}
        {estado === 'error' && <div style={st.err}>❌ {msg}</div>}
      </div>

      {/* 2. Clientes */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.h3}>2. Clientes (terceros)</h3>
          <button style={st.btnSec} onClick={previsualizar} disabled={loadingCli}>
            {loadingCli ? 'Cargando…' : 'Previsualizar'}
          </button>
        </div>
        {preview && (
          <>
            <div style={st.stats}>
              <Stat label="Total en Siigo" value={preview.total} color="#94a3b8" />
              <Stat label="Nuevos" value={preview.nuevos} color="#10b981" />
              <Stat label="Ya existen" value={preview.existentes} color="#f59e0b" />
            </div>
            {preview.nuevos > 0 && (
              <button style={st.btnPrimary} onClick={importarClientes} disabled={loadingCli}>
                {loadingCli ? 'Importando…' : `⬇ Importar ${preview.nuevos} clientes nuevos`}
              </button>
            )}
            {resCli && <div style={st.ok}>✅ Importados: {resCli.importados} · Omitidos: {resCli.omitidos}</div>}
          </>
        )}
      </div>

      {/* 2b. Productos */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.h3}>3. Productos / servicios</h3>
          <button style={st.btnSec} onClick={previsualizarProd} disabled={loadingProd}>
            {loadingProd ? 'Cargando…' : 'Previsualizar'}
          </button>
        </div>
        <p style={st.nota}>Se crea el catálogo para elegir productos al cargar cotizaciones y facturas. Siigo no entrega precio de venta por API, así que entran con precio 0 (editable después).</p>
        {previewProd && (
          <>
            <div style={st.stats}>
              <Stat label="Total en Siigo" value={previewProd.total} color="#94a3b8" />
              <Stat label="Nuevos" value={previewProd.nuevos} color="#10b981" />
              <Stat label="Ya existen" value={previewProd.existentes} color="#f59e0b" />
            </div>
            {previewProd.nuevos > 0 && (
              <button style={st.btnPrimary} onClick={importarProd} disabled={loadingProd}>
                {loadingProd ? 'Importando…' : `⬇ Importar ${previewProd.nuevos} productos nuevos`}
              </button>
            )}
            {resProd && <div style={st.ok}>✅ Importados: {resProd.importados} · Omitidos: {resProd.omitidos}</div>}
          </>
        )}
      </div>

      {/* 4 y 5. Documentos */}
      <DocSection tipo="factura" titulo="4. Facturas (con PDF)" nota="Se guarda el PDF original de cada factura + sus datos. No se re-envían a DIAN ni afectan la contabilidad." />
      <DocSection tipo="nota_credito" titulo="5. Notas crédito (con PDF)" nota="Trae el historial de notas crédito de Siigo con su PDF original y su CUDE. No se re-envían a DIAN ni afectan la contabilidad." />
      <DocSection tipo="cotizacion" titulo="6. Cotizaciones" nota="Siigo no entrega PDF de cotizaciones por API — se genera con Puppeteer para que queden autocontenidas." />

      {/* 5. Historial */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.h3}>7. Historial importado</h3>
          <div style={{ display:'flex', gap:8 }}>
            {['factura', 'nota_credito', 'cotizacion'].map(t => (
              <button key={t} onClick={() => setHistTipo(t)}
                style={{ ...st.tab, ...(histTipo === t ? st.tabActive : {}) }}>
                {t === 'factura' ? 'Facturas' : t === 'nota_credito' ? 'Notas crédito' : 'Cotizaciones'}
              </button>
            ))}
          </div>
        </div>
        {hist && (
          <div style={{ overflowX:'auto', marginTop:12 }}>
            <p style={st.hint}>{hist.total} documentos importados</p>
            <table style={st.table}>
              <thead>
                <tr>{['Número', 'Fecha', 'Cliente', 'Total', ''].map(h => <th key={h} style={st.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {hist.documentos.map(d => (
                  <tr key={d.id} style={st.tr}>
                    <td style={{ ...st.td, color:'#f1f5f9' }}>{d.prefijo || ''}{d.numero}</td>
                    <td style={st.td}>{d.fecha || '—'}</td>
                    <td style={{ ...st.td, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.cliente || '—'}</td>
                    <td style={st.td}>{fmt(d.total)}</td>
                    <td style={st.td}>
                      <button style={st.btnVer} onClick={() => verDoc(d)}>
                        {d.pdf_path ? 'Ver PDF' : 'Ver en Siigo ↗'}
                      </button>
                    </td>
                  </tr>
                ))}
                {hist.documentos.length === 0 &&
                  <tr><td colSpan={5} style={{ ...st.td, textAlign:'center', padding:24 }}>Aún no hay documentos importados de este tipo.</td></tr>}
              </tbody>
            </table>
            {hist.total > 50 && (
              <div style={st.pager}>
                <button style={st.btnVer} disabled={hist.page <= 1}
                  onClick={() => cargarHistorial(hist.page - 1)}>← Anterior</button>
                <span style={st.hint}>Página {hist.page} de {Math.ceil(hist.total / 50)}</span>
                <button style={st.btnVer} disabled={hist.page >= Math.ceil(hist.total / 50)}
                  onClick={() => cargarHistorial(hist.page + 1)}>Siguiente →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div style={st.statBox}>
    <div style={{ fontSize:26, fontWeight:700, color }}>{value}</div>
    <div style={{ fontSize:12, color:'#64748b' }}>{label}</div>
  </div>
);

const st = {
  h1:  { fontSize:24, fontWeight:700, color:'#f1f5f9', margin:'0 0 4px' },
  sub: { color:'#64748b', fontSize:14, margin:'0 0 20px' },
  card:{ background:'#1e293b', borderRadius:12, border:'1px solid #334155', padding:20, marginBottom:16 },
  cardHead:{ display:'flex', justifyContent:'space-between', alignItems:'center' },
  h3:  { color:'#f1f5f9', fontSize:15, fontWeight:600, margin:0 },
  nota:{ color:'#64748b', fontSize:12, margin:'8px 0 0' },
  btnSec: { background:'#1e3a5f', border:'1px solid #3b82f6', color:'#7dd3fc', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:500, cursor:'pointer' },
  btnPrimary: { background:'#3b82f6', color:'#fff', border:'none', borderRadius:10, padding:'12px 20px', fontSize:14, fontWeight:600, cursor:'pointer', marginTop:14 },
  btnVer: { background:'#0f172a', border:'1px solid #334155', color:'#7dd3fc', borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer' },
  ok:  { background:'#022c22', border:'1px solid #10b981', borderRadius:8, color:'#6ee7b7', padding:'10px 14px', marginTop:12, fontSize:13 },
  err: { background:'#450a0a', border:'1px solid #b91c1c', borderRadius:8, color:'#fca5a5', padding:'10px 14px', marginTop:12, fontSize:13 },
  stats: { display:'flex', gap:16, marginTop:16 },
  statBox: { background:'#0f172a', border:'1px solid #334155', borderRadius:10, padding:'14px 20px', textAlign:'center', minWidth:120 },
  barBg: { background:'#0f172a', border:'1px solid #334155', borderRadius:8, height:10, overflow:'hidden' },
  barFill: { background:'#3b82f6', height:'100%', transition:'width .3s' },
  tab: { background:'#0f172a', border:'1px solid #334155', color:'#94a3b8', borderRadius:8, padding:'6px 14px', fontSize:13, cursor:'pointer' },
  tabActive: { background:'#1e3a5f', border:'1px solid #3b82f6', color:'#7dd3fc' },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th: { color:'#64748b', fontSize:11, fontWeight:600, textAlign:'left', padding:'0 10px 10px', textTransform:'uppercase' },
  tr: { borderTop:'1px solid #334155' },
  td: { padding:'4px 10px', color:'#94a3b8' },
  hint: { color:'#475569', fontSize:12, margin:'0 0 8px' },
  pager: { display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginTop:14 },
};
